import { Site } from '@envato/foundation-types'
import * as React from 'react'
import { connect, Dispatch } from 'react-redux'
import { RouteConfigComponentProps } from 'react-router-config'
import { push, RouterAction } from 'react-router-redux'

import { openModal, toggleSidebar } from '../../../common/actions/layout'
import onSearchPageDidUpdate from '../../../common/actions/search/onSearchPageDidUpdate'
import { IRootState } from '../../../common/reducers'
import {
  IEnhancedLocation,
  ILocationParams
} from '../../../common/reducers/reactRouterLocation'
import { searchResults as searchResultsSelector } from '../../../common/selectors/searchResults'
import { hasFeature } from '../../../common/utils/features'
import { getSearchLocationParams } from '../../../common/utils/location'
import makeSearchLink from '../../../common/utils/location/makeSearchLink'
import { makeLink } from '../../../common/utils/urlHelper'
import { IPageComponent } from '../../../typings/pageComponent'
import { IRecommendedSearches } from '../../../typings/recommendedSearches'
import { IProxySearchResultsDecorated } from '../../../typings/searchResults'
import {
  ISearchHeadings,
  ISearchIngestedContent
} from '../../../typings/seoMetadata'
import { IStudioRecommendations } from '../../../typings/studioRecommendations'

type IOwnProps = RouteConfigComponentProps<ILocationParams>

interface IStateProps {
  results: IProxySearchResultsDecorated
  loading: boolean
  searchHeadings: ISearchHeadings
  studioRecommendations: IStudioRecommendations
  ingestedContent: ISearchIngestedContent | null
  currentSite: Site
  relatedSearches: IRecommendedSearches
  userHasLoadAttempt: boolean
  searchResultsViewType: string
  baseUrl: string
  timeout: boolean
  itemDetailsOpen: boolean
  previousLocation: IEnhancedLocation | null
  location: IEnhancedLocation
  experiments: any
  experimentsReady: boolean
  locationParams: ILocationParams
  makeSearchLink: (params: ILocationParams, currentSite?: Site) => string
}

interface IDispatchProps {
  onSearchPageDidUpdate: (
    locationParams: ILocationParams,
    prevUser: any,
    prevResults: any
  ) => void
  push: (path: string) => RouterAction
  openModal: (data: any) => any
  toggleSidebar: () => any
}

const mapStateToProps = (
  state: IRootState,
  ownProps: IOwnProps
): IStateProps => {
  const {
    loading,
    ingestedContent,
    relatedSearches,
    timeout,
    studioRecommendations,
    searchHeadings
  } = state.searchPage
  const { currentSite, baseUrl } = state.common
  const {
    user: {
      hasLoadAttempt,
      preferences: { searchResultsViewType }
    }
  } = state
  const { selectedItemId } = state.layout
  const { previousLocation, location } = state.router

  const { experiments } = state
  const experimentsReady = experiments.experimentsReady

  const newRoutesEnabled = hasFeature(
    'routes:new',
    currentSite,
    location,
    experiments
  )

  return {
    results: searchResultsSelector(state),
    loading,
    searchHeadings,
    studioRecommendations,
    ingestedContent,
    currentSite,
    relatedSearches,
    userHasLoadAttempt: hasLoadAttempt,
    searchResultsViewType,
    baseUrl,
    timeout,
    itemDetailsOpen: !!selectedItemId,
    previousLocation,
    location,
    experiments,
    experimentsReady,
    locationParams: getSearchLocationParams({
      location,
      currentSite,
      experiments,
      routeMatch: ownProps.match
    }),
    makeSearchLink: newRoutesEnabled ? makeSearchLink : makeLink
  }
}

const mapDispatchToProps = (dispatch: Dispatch<IRootState>): IDispatchProps => {
  return {
    onSearchPageDidUpdate: (
      locationParams,
      prevUserHasLoadAttempt,
      prevResults
    ) => {
      dispatch(onSearchPageDidUpdate(
        locationParams,
        prevUserHasLoadAttempt,
        prevResults
      ) as any)
    },
    push: (path: string) => dispatch(push(path)),
    openModal: data => dispatch(openModal(data)),
    toggleSidebar: () => dispatch(toggleSidebar())
  }
}

export type Props = IDispatchProps & IStateProps & IRootState & IOwnProps

const createSearchPageContainer = (component: React.ComponentType<Props>) => {
  const pageComponent: IPageComponent<Props> = component as IPageComponent<
    Props
  >

  return connect<IStateProps, IDispatchProps, IOwnProps, IRootState>(
    mapStateToProps,
    mapDispatchToProps
  )(pageComponent)
}

export default createSearchPageContainer
